/**
 * Module: WalletScreen
 * Role: Копилка screen — balance display + idempotent withdraw flow (FTR-1005 Task 5).
 *       History list wired to /earnings/history?limit=20 (FTR-1032 Task 3).
 *       Shared caches: ['earnings','balance'] (WalletCard), ['buyer','profile'] (ProfileScreen).
 * Uses:
 *   - atoms, @tanstack/react-query, apiFetch, useNavigate, P/S/T, formatKopecks
 *   - ./WalletScreen/HistorySection (HistorySection component + EarningsHistoryItem type)
 *   - ./WalletScreen/useWithdrawFlow (NEW/REPLAY_UNKNOWN withdraw controller)
 * Used by: screens/AppShell (routed at /wallet)
 * Glossary: ai/glossary/buyer.md
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { IOSScreen, PageHeader, Card, SectionHeader, EmptyState, Skeleton, PressBtn, Ic } from '../components/atoms';
import { P, S, T } from '../styles/tokens';
import { formatKopecks } from '../lib/format';
import { HistorySection, type EarningsHistoryItem } from './WalletScreen/HistorySection';
import {
  useWithdrawFlow,
  MIN_WITHDRAWAL_KOPECKS,
  type BalanceView,
  type ProfileView,
} from './WalletScreen/useWithdrawFlow';

// ─── Backend contracts ────────────────────────────────────────────────────────
interface EarningsHistoryResponse {
  items: EarningsHistoryItem[];
  pagination: { total: number; limit: number; offset: number; has_next: boolean };
}

const MSK_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit',
});

export function formatNextAccrualDate(isoDate: string | null, now = new Date()): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) return null;
  const parts = MSK_DATE_FORMATTER.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  const todayMsk = `${part('year')}-${part('month')}-${part('day')}`;
  const normalizedDate = match[0];
  if (normalizedDate < todayMsk) return 'начислится со дня на день';
  if (normalizedDate === todayMsk) return 'начислится сегодня';
  return `начислится ${match[3]}.${match[2]}`;
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function WalletSkeleton() {
  return (
    <div style={{ padding: '8px 16px 0' }}>
      <div style={{ borderRadius: 20, background: P.ink, padding: 20, border: 'none',
        boxShadow: '0 4px 24px rgba(25,23,23,0.28)' }}>
        <Skeleton h={13} w={130} style={{ marginBottom: 10, opacity: 0.3 }} />
        <Skeleton h={52} w={200} style={{ opacity: 0.22 }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 22 }}>
          <Skeleton h={50} style={{ flex: 1, borderRadius: 25, opacity: 0.18 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ flex: 1, background: P.card, borderRadius: 20, padding: 14,
            border: `1px solid ${P.line}` }}>
            <Skeleton h={12} w={60} style={{ marginBottom: 8 }} />
            <Skeleton h={26} w={80} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── WalletScreen ─────────────────────────────────────────────────────────────
export default function WalletScreen() {
  const navigate = useNavigate();
  const flow = useWithdrawFlow({ navigate });

  const { data, isPending, isError } = useQuery<BalanceView>({
    queryKey: ['earnings', 'balance'],
    queryFn: () => apiFetch<BalanceView>('/api/v2/buyer/earnings/balance'),
    retry: false,
  });
  const { data: profile } = useQuery<ProfileView>({
    queryKey: ['buyer', 'profile'],
    queryFn: () => apiFetch<ProfileView>('/api/v2/buyer/profile'),
    retry: false,
  });
  const { data: history, isPending: historyPending, isError: historyError } =
    useQuery<EarningsHistoryResponse>({
      queryKey: ['earnings', 'history'],
      queryFn: () => apiFetch<EarningsHistoryResponse>('/api/v2/buyer/earnings/history?limit=20'),
      retry: false,
    });

  const sbpComplete = profile?.sbp_complete ?? false;
  const availableKopecks = data?.available_kopecks ?? 0;
  const nextAccrualCopy = formatNextAccrualDate(data?.next_accrual_date ?? null);

  function handleWithdrawClick() {
    if (flow.legacyBlocked) return;
    if (!sbpComplete) { navigate('/requisites'); return; }
    if (availableKopecks < MIN_WITHDRAWAL_KOPECKS) return;
    flow.handleWithdraw();
  }
  const btnDisabled = sbpComplete
    && (availableKopecks < MIN_WITHDRAWAL_KOPECKS || flow.isSending || flow.withdrawalId !== null || flow.legacyBlocked);

  return (
    <IOSScreen tabBarSpace>
      <PageHeader title="Копилка" />
      {isPending && <WalletSkeleton />}
      {isError && !isPending && data === undefined && <EmptyState title="Копилка недоступна" subtitle="Попробуй позже" />}

      {!isPending && data !== undefined && (
        <div style={{ padding: '8px 16px 0' }}>

          {/* ── Dark hero card ── */}
          <div style={{
            borderRadius: 20, background: P.ink, padding: 20, position: 'relative',
            overflow: 'hidden', border: 'none', boxShadow: '0 4px 24px rgba(25,23,23,0.28)',
          }}>
            {/* Caveat "копилка" annotation */}
            <span style={{
              position: 'absolute', top: 18, right: 18, fontFamily: T.fontAccent,
              fontWeight: 700, fontSize: 22, color: P.terraLight,
              transform: 'rotate(8deg)', display: 'inline-block', lineHeight: 1, opacity: 0.9,
            }}>копилка</span>

            <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.60)', margin: '0 0 4px', fontWeight: 500 }}>
              Доступно к выводу
            </p>
            <p style={{ font: T.heroAmount, color: '#FFFFFF', margin: 0, letterSpacing: -0.8 }}>
              {formatKopecks(data.available_kopecks)}
            </p>
            {sbpComplete && availableKopecks > 0 && availableKopecks < MIN_WITHDRAWAL_KOPECKS && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                Вывод от {formatKopecks(MIN_WITHDRAWAL_KOPECKS)} — накопи ещё {formatKopecks(MIN_WITHDRAWAL_KOPECKS - availableKopecks)}
              </p>
            )}
            {data.pending_kopecks > 0 && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                +{formatKopecks(data.pending_kopecks)} ждут начисления
                {nextAccrualCopy && `, ${nextAccrualCopy}`}
              </p>
            )}
            {flow.success && (
              <p style={{ font: T.footnote, color: P.terraLight, margin: '8px 0 0', fontWeight: 600 }}>
                Деньги отправлены
              </p>
            )}
            {flow.pollTimedOut && !flow.success && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                Обрабатывается — уведомим в Telegram
              </p>
            )}
            {flow.isPolling && !flow.success && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                Обрабатываем…
              </p>
            )}
            {flow.error && !flow.failReason && (
              <p style={{ font: T.footnote, color: P.terraLight, margin: '8px 0 0' }}>{flow.error}</p>
            )}

            {/* Action buttons row */}
            <div style={{ display: 'flex', gap: 8, marginTop: 22 }}>
              {flow.failReason ? (
                <PressBtn bg={P.terracotta} fg="#fff" onClick={flow.handleRetry}>Попробовать ещё раз</PressBtn>
              ) : (
                <>
                  <PressBtn bg="#ffffff" fg={P.ink} disabled={btnDisabled} onClick={handleWithdrawClick}>
                    {flow.isSending ? 'Отправляем…' : 'Вывести'}
                  </PressBtn>
                  <PressBtn bg="rgba(255,255,255,0.12)" fg="#ffffff" full={false}
                    disabled={flow.isSending}
                    style={{ flexShrink: 0, paddingLeft: 20, paddingRight: 20 }}
                    onClick={() => navigate('/requisites')}>
                    Реквизиты
                  </PressBtn>
                </>
              )}
            </div>
            {flow.failReason && (
              <p style={{ font: T.footnote, color: P.terraLight, margin: '8px 0 0', textAlign: 'center' }}>
                {flow.failReason}
              </p>
            )}
          </div>

          {/* ── SBP requisites nudge ── */}
          {!sbpComplete && (
            <Card p={14} r={20} hover onClick={() => navigate('/requisites')} style={{
              marginTop: 12, background: '#FFF6E5', borderColor: 'rgba(212,137,14,0.35)',
              display: 'flex', gap: 12, alignItems: 'center',
            }}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: P.warn,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Ic name="warning" size={17} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ font: T.headline, color: S.label, margin: 0 }}>Заполни реквизиты для вывода</p>
                <p style={{ font: T.footnote, color: S.secondaryLabel, margin: '2px 0 0' }}>Банк, ФИО и телефон СБП</p>
              </div>
              <Ic name="chevR" size={14} color={P.warn} />
            </Card>
          )}

          {/* ── Stats row ── */}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {[
              { label: 'Начислено', val: data.accrued_total_kopecks },
              { label: 'Выведено', val: data.withdrawn_total_kopecks },
            ].map(({ label, val }) => (
              <div key={label} style={{ flex: 1 }}>
                <Card p={14} r={20}>
                  <p style={{ font: T.footnote, color: S.secondaryLabel, margin: '0 0 4px',
                    textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</p>
                  <p style={{ font: T.title2, color: S.label, margin: 0 }}>{formatKopecks(val)}</p>
                </Card>
              </div>
            ))}
          </div>

          <SectionHeader>История</SectionHeader>
          <HistorySection isPending={historyPending} isError={historyError} items={history?.items ?? []} />
        </div>
      )}
    </IOSScreen>
  );
}
